import React, { useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import axios from "axios";

const ENDPOINT_LABELS = {
  "LocationController@listNearbyLocations": "Nearby Locations",
  "LocationController@suggestions": "Suggestions",
  "LocationController@search": "Search",
  "LocationController@listByCategoryAndNearby": "Category & Nearby",
  "LocationController@listByViewport": "Viewport",
  "LocationController@index": "Index",
  "GridController@getNearestCell": "Nearest Cell",
  "GridController@getCellCenter": "Cell Center",
};

const ENDPOINT_COLORS = {
  "LocationController@listNearbyLocations": "warning",
  "LocationController@suggestions": "info",
  "LocationController@search": "primary",
  "LocationController@listByCategoryAndNearby": "secondary",
  "LocationController@listByViewport": "success",
  "LocationController@index": "light",
  "GridController@getNearestCell": "danger",
  "GridController@getCellCenter": "dark",
};

function shortEndpoint(name) {
  if (!name) return "—";
  return ENDPOINT_LABELS[name] || name.replace(/.*@/, "");
}

function endpointBadgeColor(name) {
  return ENDPOINT_COLORS[name] || "secondary";
}

function downloadCSV(rows) {
  const headers = [
    "ID", "Device ID", "Phone", "Registered 24h+", "DateTime", "Activity", "Params",
    "HTTP Status", "Response Time (ms)", "IP Address",
    "Result Status", "Result Count", "Result Sample", "Result Error", "Result Cell Number"
  ];

  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (s.includes(",") || s.includes('"') || s.includes("\n")) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };

  const lines = [
    headers.join(","),
    ...rows.map((r) =>
      [
        r.id,
        r.device_id,
        r.phone_number,
        r.device_registered_24h ? "yes" : "no",
        r.datetime,
        r.activity,
        r.params ? Object.entries(r.params).map(([k, v]) => `${k}=${v}`).join(" ") : "",
        r.response_status,
        r.response_time_ms,
        r.ip_address,
        r.result?.status,
        r.result?.count,
        r.result?.sample ? r.result.sample.join(" | ") : "",
        r.result?.error,
        r.result?.cell_number,
      ]
        .map(escape)
        .join(",")
    ),
  ];

  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `map-activities.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const MapActivities = () => {
  const today = new Date().toISOString().split("T")[0];
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split("T")[0];

  const [dateFrom, setDateFrom] = useState(weekAgo);
  const [dateTo, setDateTo] = useState(today);
  const [deviceIdFilter, setDeviceIdFilter] = useState("");
  const [perPage, setPerPage] = useState(100);
  const [hasDeviceId, setHasDeviceId] = useState(false);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  const API_URL = process.env.REACT_APP_API_URL;

  const fetchData = async (pageNum = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = { date_from: dateFrom, date_to: dateTo, per_page: perPage, page: pageNum };
      if (deviceIdFilter.trim()) params.device_id = deviceIdFilter.trim();
      if (hasDeviceId) params.has_device_id = true;

      const res = await axios.get(`${API_URL}admin/metrics/map-activities`, {
        params,
        withCredentials: true,
      });
      setData(res.data);
      setPage(pageNum);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    setLoading(true);
    setError(null);
    try {
      const allRows = [];
      let currentPage = 1;
      let lastPage = 1;

      do {
        const params = { date_from: dateFrom, date_to: dateTo, per_page: 500, page: currentPage };
        if (deviceIdFilter.trim()) params.device_id = deviceIdFilter.trim();
        const res = await axios.get(`${API_URL}admin/metrics/map-activities`, {
          params,
          withCredentials: true,
        });
        allRows.push(...(res.data.data || []));
        lastPage = res.data.last_page || 1;
        currentPage++;
      } while (currentPage <= lastPage);

      downloadCSV(allRows);
    } catch (e) {
      setError("Failed to download CSV");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid mt-4" dir="ltr">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <h4 className="mb-0">Map Activities</h4>
          <small className="text-muted">All map API calls — location, search, grid</small>
        </div>
        <button
          className="btn btn-success"
          onClick={handleDownloadCSV}
          disabled={loading}
          title="Download all matching rows as CSV (all pages)"
        >
          ⬇ Download CSV
        </button>
      </div>

      {/* Filters */}
      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-2">
              <label className="form-label mb-1"><strong>From</strong></label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label mb-1"><strong>To</strong></label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label mb-1"><strong>Device ID</strong> <span className="text-muted fw-normal">(optional)</span></label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Filter by device ID"
                value={deviceIdFilter}
                onChange={(e) => setDeviceIdFilter(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchData(1)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label mb-1"><strong>Has Device ID</strong></label>
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="hasDeviceId"
                  checked={hasDeviceId}
                  onChange={(e) => setHasDeviceId(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="hasDeviceId">
                  Only entries with device ID
                </label>
              </div>
            </div>
            <div className="col-md-2">
              <label className="form-label mb-1"><strong>Per Page</strong></label>
              <select
                className="form-select form-select-sm"
                value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value))}
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
            </div>
            <div className="col-md-2">
              <button
                className="btn btn-primary w-100"
                onClick={() => fetchData(1)}
                disabled={loading}
              >
                {loading ? "Loading…" : "Search"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Results */}
      {data && (
        <>
          <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
            <div>
              <span className="badge bg-secondary me-2">Total: {data.total?.toLocaleString()}</span>
              <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                Page {data.current_page} of {data.last_page}
              </span>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={data.current_page <= 1 || loading}
                onClick={() => fetchData(data.current_page - 1)}
              >
                ← Prev
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={data.current_page >= data.last_page || loading}
                onClick={() => fetchData(data.current_page + 1)}
              >
                Next →
              </button>
            </div>
          </div>

          {data.data.length === 0 ? (
            <div className="alert alert-info">No activities found for the selected range.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="table table-sm table-bordered table-hover" style={{ fontSize: "0.82rem" }}>
                <thead className="table-dark" style={{ position: "sticky", top: 0 }}>
                  <tr>
                    <th>#</th>
                    <th>Device ID</th>
                    <th>DateTime</th>
                    <th>Activity</th>
                    <th>Params</th>
                    <th>Status</th>
                    <th>ms</th>
                    <th>IP</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((row, i) => (
                    <tr
                      key={row.id}
                      className={row.response_status >= 400 ? "table-danger" : ""}
                    >
                      <td className="text-muted">{(data.current_page - 1) * data.per_page + i + 1}</td>
                      <td>
                        {row.device_id ? (
                          <span>
                            <code style={{ fontSize: "0.75rem" }}>{row.device_id}</code>
                            {row.device_registered_24h
                              ? <span className="badge bg-success ms-1" title="Device registered ≥24h before this request">✓ 24h</span>
                              : <span className="badge bg-warning text-dark ms-1" title="Device registered less than 24h before this request, or not registered">new</span>
                            }
                            {row.phone_number && (
                              <span className="d-block text-muted" style={{ fontSize: "0.75rem", marginTop: "2px" }}>
                                📞 {row.phone_number}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <span className="text-muted">{row.datetime?.slice(0, 10)}</span>
                        {" "}
                        <strong>{row.datetime?.slice(11, 19)}</strong>
                      </td>
                      <td>
                        <span className={`badge bg-${endpointBadgeColor(row.activity)} text-${endpointBadgeColor(row.activity) === "light" ? "dark" : "white"}`}>
                          {shortEndpoint(row.activity)}
                        </span>
                      </td>
                      <td style={{ maxWidth: "220px" }}>
                        {row.params && Object.keys(row.params).length > 0 ? (
                          <span>
                            {Object.entries(row.params).map(([k, v]) => (
                              <span key={k} className="badge bg-light text-dark border me-1" style={{ fontSize: "0.72rem" }}>
                                <strong>{k}:</strong> {String(v).length > 30 ? String(v).slice(0, 30) + "…" : v}
                              </span>
                            ))}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        <span
                          className={`badge bg-${
                            row.response_status < 300
                              ? "success"
                              : row.response_status < 400
                              ? "warning"
                              : "danger"
                          }`}
                        >
                          {row.response_status}
                        </span>
                      </td>
                      <td>{row.response_time_ms ?? "—"}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{row.ip_address || "—"}</td>
                      <td style={{ maxWidth: "260px" }}>
                        {row.result ? (
                          <span>
                            <span
                              className={`badge bg-${row.result.status === "success" ? "success" : "danger"} me-1`}
                            >
                              {row.result.status}
                            </span>
                            {row.result.count !== undefined && (
                              <span className="badge bg-secondary me-1">
                                {row.result.count} results
                              </span>
                            )}
                            {row.result.cell_number && (
                              <span className="badge bg-info text-dark me-1">
                                #{row.result.cell_number}
                              </span>
                            )}
                            {row.result.sample && row.result.sample.length > 0 && (
                              <span className="text-muted" style={{ fontSize: "0.75rem", display: "block", marginTop: "2px" }}>
                                {row.result.sample.join(" · ")}
                              </span>
                            )}
                            {row.result.error && (
                              <span
                                className="text-danger d-block"
                                style={{ fontSize: "0.75rem" }}
                                title={row.result.error}
                              >
                                ⚠ {row.result.error}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Bottom pagination */}
          <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
            <span className="text-muted" style={{ fontSize: "0.85rem" }}>
              Showing {(data.current_page - 1) * data.per_page + 1}–
              {Math.min(data.current_page * data.per_page, data.total)} of {data.total?.toLocaleString()}
            </span>
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={data.current_page <= 1 || loading}
                onClick={() => fetchData(data.current_page - 1)}
              >
                ← Prev
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={data.current_page >= data.last_page || loading}
                onClick={() => fetchData(data.current_page + 1)}
              >
                Next →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MapActivities;
